import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight } from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import { serviceApi, projectApi, assetUrl, contactApi } from '../services/api'

const DynamicIcon = ({ name, className }: { name: string | null; className?: string }) => {
  if (!name) return <div className={className} />
  
  if (name.includes('/') || name.includes('.')) {
     return <img src={assetUrl(name)} alt="" className={`object-contain ${className}`} />
  }
  
  // @ts-expect-error LucideIcons is a module
  const Icon = LucideIcons[name]
  if (Icon) return <Icon className={className} />
  
  return <div className={className} />
}

type Service = {
  id: number
  title: string
  description: string
  full_description?: string | null
  features?: any[]
  benefits?: string | null
  color?: string | null
  gradient?: string | null
  featured_image?: string | null
  image?: string | null
  image_url?: string | null
  gallery_images?: string[]
  detail_page_slug?: string
  price?: number | string | null
  duration?: string | null
  page_views?: number | null
  case_study_ids?: number[] | null
}

type ProjectBrief = { id: number; title?: string; image?: string | null; image_url?: string | null; slug?: string }

const ServiceDetail: React.FC = () => {
  const { slug } = useParams()
  const [service, setService] = useState<Service | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [cases, setCases] = useState<ProjectBrief[]>([])
  const [index, setIndex] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', message: '' })

  const { scrollYProgress } = useScroll()
  const yHero = useTransform(scrollYProgress, [0, 1], [0, -120])
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 15])

  useEffect(() => {
    const toSlug = (title: string, id?: number | string) => {
      const base = String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'service'
      return id != null ? `${base}-${id}` : base
    }
    const fetch = async () => {
      let s: Service | null = null
      const currentSlug = String(slug || '')

      if (currentSlug) {
        try {
          const bySlug = await serviceApi.getBySlug(currentSlug)
          if (bySlug?.success) s = bySlug.data as Service
        } catch { /* continue */ }
      }

      if (!s && currentSlug) {
        try {
          const idMatch = currentSlug.match(/-(\d+)$/)
          const maybeId = idMatch ? Number(idMatch[1]) : (/^\d+$/.test(currentSlug) ? Number(currentSlug) : null)
          if (maybeId != null && !Number.isNaN(maybeId)) {
            const byId = await serviceApi.getById(maybeId)
            if (byId?.success) s = byId.data as Service
          }
        } catch { /* continue */ }
      }

      if (!s && currentSlug) {
        try {
          const res = await serviceApi.getAll()
          if (res?.success && Array.isArray(res.data)) {
            const list = res.data as Service[]
            s = list.find((r) => String(r.detail_page_slug || toSlug(String(r.title || ''), r.id)) === currentSlug) || null
          }
        } catch { /* continue */ }
      }

      if (!s) {
        setService(null)
        setNotFound(true)
        return
      }

      setService(s)

      try {
        const pr = await projectApi.getAll()
        if (pr?.success && Array.isArray(pr.data)) {
          const mapped: ProjectBrief[] = pr.data.map((p: unknown) => {
            const row = p as { id: number; title?: unknown; image?: unknown; image_url?: unknown; slug?: unknown }
            return {
              id: row.id,
              title: String(row.title || ''),
              image: (row.image as string | null | undefined) ?? null,
              image_url: (row.image_url as string | null | undefined) ?? null,
              slug: String(row.slug || '')
            }
          })
          const ids = Array.isArray(s?.case_study_ids) ? s?.case_study_ids : []
          if (ids && ids.length) {
            setCases(mapped.filter((p) => ids.includes(p.id)))
          }
        }
      } catch { /* ignore related fetch errors */ }
    }
    fetch()
  }, [slug])

  const gallery = useMemo(() => {
    const imgs = service?.gallery_images || []
    const fallbacks = [service?.featured_image, service?.image, service?.image_url].filter(Boolean) as string[]
    return imgs.length ? imgs : fallbacks
  }, [service])

  const currentImage = gallery[index] || null

  const onPrev = () => setIndex((i) => (i - 1 + gallery.length) % Math.max(gallery.length, 1))
  const onNext = () => setIndex((i) => (i + 1) % Math.max(gallery.length, 1))

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSubmitting(true)
      await contactApi.submit({ name: form.name, email: form.email, subject: `Inquiry: ${service?.title || 'Service'}`, message: form.message })
      setForm({ name: '', email: '', message: '' })
      setSubmitting(false)
    } catch { setSubmitting(false) }
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-[#030014] text-white pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-gray-400">{notFound ? 'Service not found' : 'Loading service...'}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <style>{`
        .service-overview-content blockquote,
        .service-overview-content [style*="border-left"],
        .service-overview-content [style*="border-left:"],
        .service-overview-content [style*="border-left-color"] {
          border-left: none !important;
          box-shadow: none !important;
        }
      `}</style>
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <motion.div 
          style={{ 
            y: yHero as unknown as number, 
            rotate,
          }} 
          className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-amber-600/10 blur-[120px] rounded-full" 
        />
        <motion.div 
          style={{ 
            y: yHero as unknown as number,
            rotate: -rotate,
          }} 
          className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] bg-amber-500/10 blur-[120px] rounded-full" 
        />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="h-full w-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='7' cy='7' r='7'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-32 pb-16">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-6xl md:text-8xl font-black mb-8 leading-tight"
            >
              <span className="text-white">{service.title.split(' ')[0]}</span>
              <br />
              <span className="text-amber-500">{service.title.split(' ').slice(1).join(' ')}</span>
            </motion.h1>
            
          </div>

          {/* Hero Image Gallery */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="relative max-w-6xl mx-auto"
          >
            <div className="aspect-video overflow-hidden relative">
              <AnimatePresence initial={false} mode="wait">
                {currentImage && (
                  <motion.img
                    key={currentImage}
                    src={assetUrl(currentImage)}
                    className="w-full h-full object-contain"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </AnimatePresence>
              
              {/* Gallery Controls */}
              {gallery.length > 1 && (
                <div className="absolute bottom-8 right-8 flex gap-3 z-20">
                  <button 
                    onClick={onPrev} 
                    className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-md border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-black transition-all hover:scale-110 active:scale-95"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button 
                    onClick={onNext} 
                    className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-md border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-black transition-all hover:scale-110 active:scale-95"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
              )}

              {/* Image Counter */}
              {gallery.length > 1 && (
                <div className="absolute top-8 right-8 px-4 py-2 bg-black/50 backdrop-blur-md border border-white/10 rounded-full">
                  <span className="text-sm font-bold text-white">
                    <span className="text-amber-500">{index + 1}</span>/{gallery.length}
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="text-xl md:text-2xl text-gray-300 leading-relaxed max-w-4xl mx-auto mt-10 text-center"
          >
            {service.description}
          </motion.p>
        </div>
      </section>

        {/* Features Section */}
      {service.full_description && (
        <section className="relative py-20">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black mb-8">
                Service <span className="text-amber-500">Overview</span>
              </h2>
              <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="service-overview-content prose prose-invert prose-lg max-w-none text-gray-300 text-xl leading-relaxed"
              dangerouslySetInnerHTML={{ __html: service.full_description }} 
            />
          </div>
        </section>
      )}

      {/* Key Features Grid */}
      {Array.isArray(service.features) && service.features.length > 0 && (
        <section className="relative py-20 bg-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black mb-8">
                Key <span className="text-amber-500">Features</span>
              </h2>
              <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {service.features.map((f: any, i) => (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-8 bg-black/30 border border-white/10 rounded-2xl hover:border-amber-500/50 hover:bg-amber-500/5 hover:-translate-y-2 transition-all duration-300 group backdrop-blur-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-amber-500/10 text-amber-500 group-hover:scale-110 transition-transform duration-300">
                       {f.icon ? (
                         <DynamicIcon name={f.icon} className="w-6 h-6" />
                       ) : (
                         <Sparkles size={24} />
                       )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-black text-white text-lg mb-2">{typeof f === 'string' ? f : f.title}</h4>
                      {typeof f !== 'string' && f.description && <p className="text-gray-400 leading-relaxed">{f.description}</p>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Case Studies */}
      {cases.length > 0 && (
        <section className="relative py-20 bg-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black mb-8">
                Related <span className="text-amber-500">Projects</span>
              </h2>
              <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {cases.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link to={`/projects/${p.slug || p.id}`} className="group block">
                    <div className="aspect-video bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all duration-300">
                      <img 
                        src={assetUrl(p.image || p.image_url || null)} 
                        alt="" 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                      />
                    </div>
                    <div className="mt-4">
                      <h3 className="text-xl font-black text-white group-hover:text-amber-500 transition-colors">{p.title}</h3>
                      <div className="flex items-center gap-2 mt-2 text-amber-500 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all">
                        View Project <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 backdrop-blur-xl border border-amber-500/30 rounded-3xl p-12 text-center"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-8">
              Ready to Get <span className="text-amber-500">Started?</span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              Let's discuss how we can help you achieve your goals with our professional {service.title} services.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-amber-500 text-black font-black rounded-full hover:bg-amber-400 transition-all duration-300 flex items-center gap-3 group"
              >
                Contact Us
                <ArrowRight className="group-hover:translate-x-2 transition-transform" size={20} />
              </button>
              <Link 
                to="/our-services"
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transition-colors"
              >
                View Other Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="relative py-20">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-8">
              Get in <span className="text-amber-500">Touch</span>
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
          >
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm text-amber-400 font-medium">Name</label>
                  <input 
                    value={form.name} 
                    onChange={(e) => setForm({ ...form, name: e.target.value })} 
                    placeholder="John Doe" 
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all outline-none" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-amber-400 font-medium">Email</label>
                  <input 
                    value={form.email} 
                    onChange={(e) => setForm({ ...form, email: e.target.value })} 
                    placeholder="john@example.com" 
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all outline-none" 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-amber-400 font-medium">Message</label>
                <textarea 
                  value={form.message} 
                  onChange={(e) => setForm({ ...form, message: e.target.value })} 
                  placeholder="Tell us about your project needs..." 
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white h-40 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all outline-none resize-none" 
                />
              </div>
              <button 
                type="submit"
                disabled={submitting} 
                className="w-full py-4 bg-amber-500 text-black font-black rounded-xl hover:bg-amber-400 transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-3 group"
              >
                {submitting ? 'Sending...' : (
                  <>
                    Send Message
                    <ArrowRight className="group-hover:translate-x-2 transition-transform" size={20} />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default ServiceDetail
